import { Button, Drawer, Flex, MultiSelect, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';
import { UserRowWithActions } from '@dealstack/frontend/shared/components/UserRowWithActions/UserRowWithActions.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { MipClassifierRes } from '@dealstack/shared/lib/generated';
import { attachInvestorsToClassifier } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/services/mipClassifiers.service.ts';

interface Props {
	investorId: string;
	investorName: string;
	availableMipClassifiers: MipClassifierRes[];
	hasPermission: boolean;
	opened: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export const ManageInvestorMipClassifiersDrawer = ({
	investorId,
	investorName,
	availableMipClassifiers,
	hasPermission,
	opened,
	onClose,
	onSuccess,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const [selectedMipClassifierIds, setSelectedMipClassifierIds] = useState<string[]>([]);
	const [lastAddedMipClassifierName, setLastAddedMipClassifierName] = useState<string | null>(
		null
	);

	const attachMutation = useMutation({
		mutationFn: attachInvestorsToClassifier,
	});

	const mipClassifierOptions = useMemo(
		() => availableMipClassifiers.map((c) => ({ value: c.id, label: c.name })),
		[availableMipClassifiers]
	);

	const hasNoAvailableMipClassifiers = mipClassifierOptions.length === 0;

	useEffect(() => {
		if (!opened) {
			setSelectedMipClassifierIds([]);
			setLastAddedMipClassifierName(null);
		}
	}, [opened]);

	const handleSelectionChange = (value: string[]) => {
		const lastAddedId = value[value.length - 1];
		const name = mipClassifierOptions.find((opt) => opt.value === lastAddedId)?.label ?? null;
		setLastAddedMipClassifierName(name);
		setTimeout(() => setLastAddedMipClassifierName(null), 3000);
		setSelectedMipClassifierIds(value);
	};

	const removeFromSelection = (mipClassifierId: string) => {
		setSelectedMipClassifierIds((prev) => prev.filter((id) => id !== mipClassifierId));
	};

	const selectedMipClassifiers = availableMipClassifiers.filter((c) =>
		selectedMipClassifierIds.includes(c.id)
	);

	const onSubmit = async () => {
		if (!selectedMipClassifierIds.length) {
			return;
		}

		try {
			await Promise.all(
				selectedMipClassifierIds.map((mipClassifierId) =>
					attachMutation.mutateAsync({
						mipClassifierId,
						body: { investorIds: [investorId] },
					})
				)
			);
			showToastV2({
				state: 'success',
				message: tMipManage('root.investor.mip_classifiers.drawer.toast.success'),
			});
			onClose();
			onSuccess?.();
		} catch {
			showToastV2({
				state: 'error',
				message: tMipManage('root.investor.mip_classifiers.drawer.toast.error'),
			});
		}
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tMipManage('root.investor.mip_classifiers.drawer.title')}
		>
			<Flex direction="column" justify="space-between" h="100%">
				<Flex direction="column" gap="1rem">
					<MultiSelect
						data={mipClassifierOptions}
						value={selectedMipClassifierIds}
						onChange={handleSelectionChange}
						label={
							<Flex align="center" justify="space-between" gap="0.25rem">
								{tMipManage('root.investor.mip_classifiers.drawer.form.label')}
								{lastAddedMipClassifierName && (
									<Flex align="center" gap="0.25rem" maw="60%">
										<Icon variant="holdings-checkmark" size="md" />
										<Flex align="center" gap="0.25rem">
											<Text fz="sm" fw={400} truncate lineClamp={1}>
												{lastAddedMipClassifierName}
											</Text>
											<Text
												fz="sm"
												fw={400}
												c="dark.0"
												style={{ whiteSpace: 'nowrap' }}
											>
												{tMipManage(
													'root.investor.mip_classifiers.drawer.form.added_to_list'
												)}
											</Text>
										</Flex>
									</Flex>
								)}
							</Flex>
						}
						labelProps={{ style: { width: '100%' } }}
						placeholder={tMipManage(
							'root.investor.mip_classifiers.drawer.form.placeholder'
						)}
						disabled={hasNoAvailableMipClassifiers || !hasPermission}
						rightSection={<Icon variant="caret-down" size="lg" />}
						styles={{ rightSection: { pointerEvents: 'none' } }}
						size="md"
						clearable
						searchable
					/>

					{hasNoAvailableMipClassifiers && (
						<InfoBox
							iconVariant="information"
							alignContent="center"
							description={tMipManage(
								'root.investor.mip_classifiers.drawer.no_classifiers_warning'
							)}
						/>
					)}

					{!!selectedMipClassifiers.length && (
						<Flex direction="column">
							<Text fw={600}>
								{tMipManage('root.investor.mip_classifiers.drawer.selected_title')}
							</Text>
							<Text c="gray.9" mt="0.25rem" mb="0.5rem">
								{tMipManage(
									'root.investor.mip_classifiers.drawer.selected_description',
									{
										name: investorName,
									}
								)}
							</Text>
							{selectedMipClassifiers.map((mipClassifier) => (
								<UserRowWithActions
									key={mipClassifier.id}
									displayName={mipClassifier.name}
									onUserDeleteClick={() => removeFromSelection(mipClassifier.id)}
									hasEditPermission={hasPermission}
									showInitialsAvatar={false}
								/>
							))}
						</Flex>
					)}
				</Flex>

				<Button
					onClick={onSubmit}
					disabled={
						attachMutation.isPending ||
						!selectedMipClassifierIds.length ||
						!hasPermission
					}
				>
					{tMipManage('root.investor.mip_classifiers.drawer.submit_button')}
				</Button>
			</Flex>
		</Drawer>
	);
};
